import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useFont } from "../contexts/FontContext";
import {
  FolderIcon,
  UserIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  AcademicCapIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  TagIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";
import { getImageUrl } from "../services/api";
import FloatingChatButton from "./FloatingChatButton";
import ChatPopup from "./ChatPopup";
import api from "../services/api";
import {
  getSiteBranding,
  updateDocumentHead,
  SiteBranding,
} from "../services/siteBrandingService";

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.FC<{ className?: string }>;
  badge?: number;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const { fontFamily, regularClass } = useFont();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [siteBranding, setSiteBranding] = useState<SiteBranding>({
    siteName: "ເພັດທະບຸນ ສະຫວັດ",
    siteTitle: "ເພັດທະບຸນ ສະຫວັດ ສູນການຮຽນຮູ້",
    siteFavicon: "/favicon.png",
    navbarLogo: undefined,
    footerLogo: undefined,
    favicon: undefined,
  });

  // Fetch unread count for admin
  useEffect(() => {
    if (user && isAdmin) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Check every 30 seconds
      return () => clearInterval(interval);
    }
  }, [user, isAdmin]);

  // Fetch site branding
  useEffect(() => {
    const fetchSiteBranding = async () => {
      try {
        const branding = await getSiteBranding();
        // console.log('Site branding loaded:', branding); // Debug log
        setSiteBranding(branding);

        // Update document head with branding
        updateDocumentHead(branding);
      } catch (error) {
        console.error("Error fetching site branding:", error);
        // Keep default values if fetch fails
      }
    };

    fetchSiteBranding();
  }, []);

  // Update document title when site branding changes
  useEffect(() => {
    if (siteBranding.siteTitle) {
      document.title = siteBranding.siteTitle;
    }
  }, [siteBranding.siteTitle]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.getElementById("profile-dropdown");
      const profileImage = document.getElementById("profile-image-button");

      if (
        dropdown &&
        profileImage &&
        !dropdown.contains(event.target as Node) &&
        !profileImage.contains(event.target as Node)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get("/messages/unread-count");
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    setIsProfileDropdownOpen(false);
    navigate("/login");
  };

  const navigateToProfile = () => {
    setIsProfileDropdownOpen(false);
    navigate(isAdmin ? "/admin/profile" : "/profile");
  };

  const navigateToSettings = () => {
    setIsProfileDropdownOpen(false);
    navigate("/settings");
  };

  const isActive = (path: string) => location.pathname === path;

  const studentNavItems: NavItem[] = [
    // { path: '/videos', label: 'ວິດີໂອ', icon: VideoCameraIcon },
    // { path: '/progress', label: 'ຄວາມຄືບໜ້າ', icon: ChartBarIcon },
    // { path: '/certificates', label: 'ໃບຢັ້ງຢືນ', icon: AcademicCapIcon },
  ];

  const adminNavItems: NavItem[] = [
    { path: "/admin/videos", label: "ໝວດໝູ່ໂຟລເດີ", icon: FolderIcon },
    // { path: '/admin/categories', label: 'ໝວດໝູ່', icon: TagIcon },
    { path: "/admin/users", label: "ຜູ້ໃຊ້", icon: UsersIcon },
    //{ path: '/admin/certificates', label: 'ໃບຢັ້ງຢືນ', icon: AcademicCapIcon },
    {
      path: "/admin/activity",
      label: "ກິດຈະກຳ",
      icon: ClipboardDocumentListIcon,
    },
    {
      path: "/admin/landing-page",
      label: "ແກ້ໄຂຫນ້າຫຼັກ",
      icon: Cog6ToothIcon,
    },
    {
      path: "/admin/chat",
      label: "ຂໍ້ຄວາມ",
      icon: ChatBubbleLeftRightIcon,
      badge: unreadCount,
    },
  ];

  const navItems = isAdmin ? adminNavItems : studentNavItems;

  // Don't show floating chat button on chat page
  const showFloatingChat = !location.pathname.includes("/chat");

  return (
    <div
      className={`min-h-screen bg-gray-50 ${regularClass} flex flex-col`}
      style={{ fontFamily }}
    >
      {/* Navigation */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-gradient-to-r from-indigo-700 to-purple-700 shadow-xl backdrop-blur-md"
            : "bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg"
        }`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16">
          <div className="flex justify-between h-14 sm:h-16">
            <div className="flex items-center min-w-0 flex-1">
              <div className="flex-shrink-0 flex items-center">
                <Link
                  to={isAdmin ? "/admin/analytics" : "/dashboard"}
                  className="flex items-center"
                >
                  {siteBranding.navbarLogo ? (
                    <img
                      src={siteBranding.navbarLogo}
                      alt="Logo"
                      className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
                    />
                  ) : (
                    <AcademicCapIcon className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                  )}
                  <span className="ml-2 text-sm sm:text-base lg:text-lg xl:text-xl font-bold text-white truncate max-w-[200px] sm:max-w-none drop-shadow-sm">
                    {siteBranding.siteName || "ເພັດທະບຸນ ສະຫວັດ"}
                  </span>
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden lg:ml-6 lg:flex lg:space-x-2 xl:space-x-4">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-2 xl:px-3 py-2 rounded-md text-xs xl:text-sm font-medium relative transition-all duration-200 ${
                      isActive(item.path)
                        ? "bg-white/20 text-white shadow-sm"
                        : "text-indigo-100 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <item.icon
                      className={`h-4 w-4 xl:h-5 xl:w-5 mr-1 xl:mr-2 ${isActive(item.path) ? "text-white" : "text-indigo-200"}`}
                    />
                    <span className="hidden xl:inline">{item.label}</span>
                    {item.badge && item.badge > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-4 w-4 xl:h-5 xl:w-5 flex items-center justify-center">
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>

            <div className="hidden lg:ml-6 lg:flex lg:items-center flex-shrink-0">
              <div className="flex items-center relative">
                <button
                  id="profile-image-button"
                  onClick={() =>
                    setIsProfileDropdownOpen(!isProfileDropdownOpen)
                  }
                  className="flex items-center focus:outline-none bg-white/10 rounded-full p-1 transition-all duration-200 hover:bg-white/20"
                >
                  <div className="flex items-center space-x-1 xl:space-x-2 px-1 xl:px-2">
                    {user?.profile_image ? (
                      <img
                        src={getImageUrl(user.profile_image)}
                        alt="Profile"
                        className="w-6 h-6 xl:w-8 xl:h-8 rounded-full border-2 border-white/50"
                      />
                    ) : (
                      <UserIcon className="w-6 h-6 xl:w-8 xl:h-8 text-white p-1" />
                    )}
                    <span className="text-xs xl:text-sm font-medium text-white hidden xl:block truncate">
                      {user?.name?.split(" ")[0]}
                    </span>
                    <ChevronDownIcon className="h-3 w-3 xl:h-4 xl:w-4 text-indigo-200" />
                  </div>
                </button>

                {/* Profile Dropdown */}
                {isProfileDropdownOpen && (
                  <div
                    id="profile-dropdown"
                    className="absolute right-0 top-12 w-48 xl:w-56 py-2 mt-1 bg-white rounded-lg shadow-xl z-20 ring-1 ring-black ring-opacity-5 transition-all duration-200 transform origin-top-right"
                  >
                    <div className="px-3 xl:px-4 py-3 text-xs xl:text-sm text-gray-700 border-b border-gray-100">
                      <p className="font-medium text-gray-800 truncate">
                        {user?.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {user?.email}
                      </p>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={navigateToProfile}
                        className="flex w-full items-center px-3 xl:px-4 py-2 text-xs xl:text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <UserIcon className="h-3 w-3 xl:h-4 xl:w-4 mr-2 xl:mr-3 text-gray-500" />
                        ໂປຣໄຟລ໌ຂອງຂ້ອຍ
                      </button>
                      {isAdmin && (
                        <button
                          onClick={navigateToSettings}
                          className="flex w-full items-center px-3 xl:px-4 py-2 text-xs xl:text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Cog6ToothIcon className="h-3 w-3 xl:h-4 xl:w-4 mr-2 xl:mr-3 text-gray-500" />
                          ການຕັ້ງຄ່າ
                        </button>
                      )}
                    </div>
                    <div className="py-1 border-t border-gray-100">
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center px-3 xl:px-4 py-2 text-xs xl:text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <ArrowRightOnRectangleIcon className="h-3 w-3 xl:h-4 xl:w-4 mr-2 xl:mr-3 text-gray-500" />
                        ອອກຈາກລະບົບ
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center lg:hidden flex-shrink-0">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-1.5 sm:p-2 rounded-md text-indigo-100 hover:text-white hover:bg-white/10 focus:outline-none"
                title="ເປີດເມນູ"
              >
                {user?.profile_image ? (
                  <img
                    src={getImageUrl(user.profile_image)}
                    alt="Menu"
                    className="h-8 w-8 sm:h-10 sm:w-10 rounded-full border-2 border-white/50"
                  />
                ) : isMobileMenuOpen ? (
                  <XMarkIcon className="block h-8 w-8 sm:h-10 sm:w-10" />
                ) : (
                  <Bars3Icon className="block h-8 w-8 sm:h-10 sm:w-10" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white shadow-lg rounded-b-lg overflow-hidden animate-fadeIn">
            <div className="pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block pl-3 sm:pl-4 pr-4 py-2.5 sm:py-2 text-sm sm:text-base font-medium relative ${
                    isActive(item.path)
                      ? "bg-indigo-50 text-indigo-700 border-l-4 border-indigo-500"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-4 border-transparent"
                  }`}
                >
                  <div className="flex items-center">
                    <item.icon
                      className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 ${isActive(item.path) ? "text-indigo-600" : "text-gray-500"}`}
                    />
                    {item.label}
                    {item.badge && item.badge > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center">
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
            <div className="pt-4 pb-3 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center px-3 sm:px-4 py-3">
                <div className="flex-shrink-0">
                  {user?.profile_image ? (
                    <img
                      src={getImageUrl(user.profile_image)}
                      alt="Profile"
                      className="h-10 w-10 sm:h-12 sm:w-12 rounded-full border-2 border-indigo-200"
                    />
                  ) : (
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                      <UserIcon className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-500" />
                    </div>
                  )}
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <div className="text-sm sm:text-base font-medium text-gray-800 truncate">
                    {user?.name}
                  </div>
                  <div className="text-xs sm:text-sm font-medium text-gray-500 truncate">
                    {user?.email}
                  </div>
                </div>
              </div>
              <div className="mt-3 space-y-1 border-t border-gray-200">
                <Link
                  to={isAdmin ? "/admin/profile" : "/profile"}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                >
                  <UserIcon className="mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
                  ໂປຣໄຟລ໌
                </Link>
                {isAdmin && (
                  <Link
                    to="/settings"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  >
                    <Cog6ToothIcon className="mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
                    ການຕັ້ງຄ່າ
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                >
                  <ArrowRightOnRectangleIcon className="mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
                  ອອກຈາກລະບົບ
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main content */}
      <main className="py-4 sm:py-6 flex-1 w-full px-4 sm:px-6 lg:px-10 xl:px-16">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg text-white py-4 sm:py-6">
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="flex items-center mb-2 sm:mb-0">
              {siteBranding.footerLogo ? (
                <img
                  src={siteBranding.footerLogo}
                  alt="Logo"
                  className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 object-contain mr-2"
                />
              ) : (
                <AcademicCapIcon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-white mr-2" />
              )}
              <span className="text-xs sm:text-sm lg:text-base font-medium truncate">
                {siteBranding.siteName}
              </span>
            </div>
            <div className="text-xs sm:text-sm text-white">
              © 2024 ສະຫງວນລິຂະສິດ. ສະຫງວນສິດທັງຫມົດ.
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Chat Button */}
      {showFloatingChat && (
        <FloatingChatButton onOpen={() => setIsChatOpen(true)} />
      )}

      {/* Chat Popup */}
      <ChatPopup isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </div>
  );
};

export default Layout;
